import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CalendarIcon, Send, CheckCircle } from "lucide-react";
import { format } from "date-fns";

// Animation constants (extracted to prevent inline object creation)
const SUCCESS_INITIAL = { opacity: 0, scale: 0.9 };
const SUCCESS_ANIMATE = { opacity: 1, scale: 1 };
const SUCCESS_TRANSITION = { duration: 0.5 };
const HEADER_INITIAL = { opacity: 0, y: 30 };
const HEADER_ANIMATE = { opacity: 1, y: 0 };
const HEADER_EMPTY = {};
const HEADER_TRANSITION = { duration: 0.6 };
const FORM_INITIAL = { opacity: 0, y: 40 };
const FORM_TRANSITION = { duration: 0.6, delay: 0.2 };
const VIEW_MARGIN = { once: true, margin: "-100px" };

const services = [
  { id: "remodel", name: "Full Remodels" },
  { id: "stucco", name: "Stucco" },
  { id: "tile", name: "Tile & Finishing" },
  { id: "concrete", name: "Concrete" },
  { id: "stone", name: "Stone Masonry" },
  { id: "painting", name: "Painting" },
  { id: "other", name: "Other Services" },
];

export default function QuoteSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, VIEW_MARGIN);
  
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    address: "",
    notes: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const getServiceName = (id) => {
    const service = services.find(s => s.id === id);
    return service ? service.name : id;
  };

  // Fire Meta Pixel Lead event ONLY when the confirmation message is displayed.
  // Runs after submitted becomes true (i.e., success screen has rendered).
  // Does NOT fire on form open, submit click, validation errors, or failed submissions.
  useEffect(() => {
    if (submitted && typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Lead", {
        content_name: "Quote Request",
        content_category: getServiceName(formData.service_type),
        currency: "USD",
        value: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast.error("Please select a preferred date for your quote.");
      return;
    }
    
    if (!formData.name || !formData.phone || !formData.service_type || !formData.address) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Create form data for Formspree
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("email", formData.email || "Not provided");
      formDataToSend.append("service", getServiceName(formData.service_type));
      formDataToSend.append("address", formData.address);
      formDataToSend.append("preferred_date", format(selectedDate, "MMMM d, yyyy"));
      formDataToSend.append("notes", formData.notes || "None");
      formDataToSend.append("_subject", `New Quote Request - ${getServiceName(formData.service_type)} - ${formData.name}`);

      // Send to Formspree (this sends email to you)
      const formspreeId = process.env.REACT_APP_FORMSPREE_ID;
      const response = await fetch(`https://formspree.io/f/${formspreeId}`, {
        method: "POST",
        body: formDataToSend,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        // Fire GA4 generate_lead event only after successful submission
        // Uses the existing global gtag with Measurement ID G-EFREZHZH9G
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "generate_lead", {
            send_to: "G-EFREZHZH9G",
            currency: "USD",
            value: 0,
            service_type: getServiceName(formData.service_type),
            form_location: "quote_section",
            transport_type: "beacon",
            debug_mode: true,
          });
        }
        
        toast.success("Quote requested! We will contact you shortly.");
        setSubmitted(true);
        
        // Reset form after 5 seconds
        setTimeout(() => {
          setFormData({
            name: "",
            phone: "",
            email: "",
            service_type: "",
            address: "",
            notes: "",
          });
          setSelectedDate(undefined);
          setSubmitted(false);
        }, 5000);
      } else {
        throw new Error("Form submission failed");
      }
    } catch (error) {
      toast.error("Failed to submit quote. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success message after submission
  if (submitted) {
    return (
      <section
        id="quote"
        className="py-24 md:py-32 bg-[#F5F5F4]"
        data-testid="quote-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={SUCCESS_INITIAL}
            animate={SUCCESS_ANIMATE}
            transition={SUCCESS_TRANSITION}
            className="bg-white border border-[#E7E5E4] p-12 md:p-16 text-center"
          >
            <div className="w-20 h-20 bg-[#166534] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-white" size={40} />
            </div>
            <h2 className="font-['Outfit'] text-3xl font-medium text-[#1C1917] mb-4">
              Quote Request Received!
            </h2>
            <p className="text-[#57534E] text-lg mb-2">
              Thank you, <span className="font-medium text-[#1C1917]">{formData.name}</span>!
            </p>
            <p className="text-[#57534E] mb-6">
              We've received your request for <span className="font-medium">{getServiceName(formData.service_type)}</span> 
              {" "}and will contact you at <span className="font-medium">{formData.phone}</span> within 24 hours.
            </p>
            <p className="text-sm text-[#A8A29E]">
              This form will reset in a few seconds...
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="quote"
      className="py-24 md:py-32 bg-[#F5F5F4]"
      data-testid="quote-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={HEADER_INITIAL}
          animate={isInView ? HEADER_ANIMATE : HEADER_EMPTY}
          transition={HEADER_TRANSITION}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#57534E] mb-4 block">
            Get Started
          </span>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-[#1C1917] mb-4">
            Schedule a Free Quote
          </h2>
          <p className="text-[#57534E] max-w-2xl mx-auto">
            <strong className="text-[#166534]">Note:</strong> Scheduling may vary as we finish up current projects. 
            We typically need 2-3 days to wrap up ongoing work before starting new jobs. 
            Select a preferred date and we'll confirm availability.
          </p>
        </motion.div>

        <motion.div
          initial={FORM_INITIAL}
          animate={isInView ? HEADER_ANIMATE : HEADER_EMPTY}
          transition={FORM_TRANSITION}
          className="bg-white border border-[#E7E5E4] p-8 md:p-12"
        >
          <form onSubmit={handleSubmit}>
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left: Calendar */}
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <CalendarIcon className="text-[#166534]" size={20} />
                  <h3 className="font-['Outfit'] text-xl font-medium text-[#1C1917]">
                    Select Preferred Date
                  </h3>
                </div>
                <div className="flex justify-center lg:justify-start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date()}
                    className="border border-[#E7E5E4] p-4"
                    data-testid="quote-calendar"
                  />
                </div>
                {selectedDate && (
                  <p className="mt-4 text-[#57534E] text-center lg:text-left">
                    Selected: <span className="text-[#166534] font-medium">{format(selectedDate, "MMMM d, yyyy")}</span>
                  </p>
                )}
              </div>

              {/* Right: Form */}
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-[#1C1917] font-medium mb-2 block">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Smith"
                      className="border-[#E7E5E4] rounded-none h-12 focus:border-[#166534] focus:ring-[#166534]"
                      required
                      data-testid="quote-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#1C1917] font-medium mb-2 block">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="(801) 555-0123"
                      className="border-[#E7E5E4] rounded-none h-12 focus:border-[#166534] focus:ring-[#166534]"
                      required
                      data-testid="quote-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#1C1917] font-medium mb-2 block">
                    Email (Optional)
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="border-[#E7E5E4] rounded-none h-12 focus:border-[#166534] focus:ring-[#166534]"
                    data-testid="quote-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-[#1C1917] font-medium mb-2 block">
                    Service Type *
                  </Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={handleServiceChange}
                    required
                  >
                    <SelectTrigger
                      className="border-[#E7E5E4] rounded-none h-12 focus:border-[#166534] focus:ring-[#166534]"
                      data-testid="quote-service-select"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="address" className="text-[#1C1917] font-medium mb-2 block">
                    Project Address *
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="123 Main St, Santaquin, UT"
                    className="border-[#E7E5E4] rounded-none h-12 focus:border-[#166534] focus:ring-[#166534]"
                    required
                    data-testid="quote-address-input"
                  />
                </div>

                <div>
                  <Label htmlFor="notes" className="text-[#1C1917] font-medium mb-2 block">
                    Additional Notes
                  </Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project..."
                    rows={4}
                    className="border-[#E7E5E4] rounded-none focus:border-[#166534] focus:ring-[#166534] resize-none"
                    data-testid="quote-notes-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#166534] hover:bg-[#14532D] text-white rounded-none h-14 text-lg font-medium"
                  data-testid="quote-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Request Free Quote
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-[#57534E]">
                  We'll get back to you within 24 hours
                </p>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
